import type { HTMLAttributes, ReactNode, ThHTMLAttributes, TdHTMLAttributes } from 'react';
import { cx } from '../../lib/cx.ts';

/**
 * Dense by default: 13px body on a 36px row. Someone reading this all day
 * wants more rows on screen, not more air inside each one.
 *
 * Numeric columns pass `numeric`, which right-aligns and switches on tabular
 * figures so a column of money stays in a straight line.
 */
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="border-b border-border">
      <tr>{children}</tr>
    </thead>
  );
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody>{children}</tbody>;
}

export interface THProps extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'className'> {
  numeric?: boolean;
  children?: ReactNode;
}

export function TH({ numeric = false, children, ...props }: THProps) {
  return (
    <th
      scope="col"
      className={cx(
        'px-3 py-2 text-2xs font-medium whitespace-nowrap text-fg-subtle',
        numeric ? 'text-right' : 'text-left',
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TR({
  children,
  interactive = false,
  ...props
}: {
  children: ReactNode;
  interactive?: boolean;
} & Omit<HTMLAttributes<HTMLTableRowElement>, 'className'>) {
  return (
    <tr
      className={cx(
        'border-b border-border-subtle last:border-0',
        interactive && 'cursor-pointer transition hover:bg-surface-raised',
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export interface TDProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'className'> {
  numeric?: boolean;
  /** De-emphasised cell, for timestamps and secondary metadata. */
  muted?: boolean;
  children?: ReactNode;
}

export function TD({ numeric = false, muted = false, children, ...props }: TDProps) {
  return (
    <td
      className={cx(
        'px-3 py-2.5 whitespace-nowrap',
        numeric && 'numeric text-right',
        muted ? 'text-fg-subtle' : 'text-fg',
      )}
      {...props}
    >
      {children}
    </td>
  );
}
